import React, { useState, useEffect } from 'react';
import { youtubeGommeAdresi } from '../utils/youtube.js';
import { XMarkIcon, CheckCircleIcon, XCircleIcon } from './icons';
import { API_URL, apiFetch } from '../services/api';
import { useSocket } from '../contexts/socketContext';

interface Question {
    questionText: string;
    questionImage?: string;
    options: string[];
    correctAnswer: number;
}

interface TopicQuiz {
    _id: string;
    subject: string;
    topic: string;
    examType: string;
    questions: Question[];
    teacherId?: string;
    classId?: string;
}

interface QuizResult {
    correctCount: number;
    incorrectCount: number;
    unansweredCount: number;
    topicNet: number;
    totalQuestions: number;
    score: number;
    answers: { questionIndex: number; selectedAnswer: number; isCorrect: boolean; timeSpentSeconds?: number }[];
    correctAnswers: number[];
}

interface TopicQuizModalProps {
    quiz: TopicQuiz;
    studentId: string | number;
    studentName?: string;
    onClose: () => void;
    onComplete?: (result: QuizResult) => void;
}

const TopicQuizModal: React.FC<TopicQuizModalProps> = ({ quiz, studentId, studentName, onClose, onComplete }) => {
    const { socket } = useSocket();
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedAnswers, setSelectedAnswers] = useState<(number | null)[]>(
        new Array(quiz.questions.length).fill(null)
    );
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [result, setResult] = useState<QuizResult | null>(null);
    const [showReview, setShowReview] = useState(false);
    const [startTime] = useState(Date.now());
    const [questionStartTimes, setQuestionStartTimes] = useState<number[]>(() => {
        const arr = new Array(quiz.questions.length).fill(0);
        arr[0] = Date.now();
        return arr;
    });
    const [questionTimes, setQuestionTimes] = useState<number[]>(
        new Array(quiz.questions.length).fill(0)
    );

    // Progress bar ref - must be before any conditional returns (React hooks rules)
    const progressRef = React.useRef<HTMLDivElement>(null);

    // Signal start of quiz
    useEffect(() => {
        if (socket) {
            socket.emit('start_quiz', {
                studentId: studentId.toString(),
                studentName: studentName || 'Öğrenci',
                quizId: quiz._id,
                classId: quiz.classId,
                subject: quiz.subject,
                topic: quiz.topic
            });
        }

        return () => {
            if (socket) {
                socket.emit('stop_quiz', {
                    studentId: studentId.toString(),
                    quizId: quiz._id,
                    classId: quiz.classId
                });
            }
        };
    }, [socket, studentId, studentName, quiz]);

    const currentQuestion = quiz.questions[currentQuestionIndex];
    const totalQuestions = quiz.questions.length;
    const answeredCount = selectedAnswers.filter(a => a !== null).length;

    // Update time spent on current question when moving away
    const updateQuestionTime = () => {
        const now = Date.now();
        const duration = Math.round((now - questionStartTimes[currentQuestionIndex]) / 1000);
        const newTimes = [...questionTimes];
        newTimes[currentQuestionIndex] += duration;
        setQuestionTimes(newTimes);

        // Reset start time for the next question (or when coming back)
        const newStartTimes = [...questionStartTimes];
        newStartTimes[currentQuestionIndex] = now;
        setQuestionStartTimes(newStartTimes);
    };

    // Update progress bar width - must be before any conditional returns (React hooks rules)
    useEffect(() => {
        if (progressRef.current) {
            const width = ((currentQuestionIndex + 1) / totalQuestions) * 100;
            progressRef.current.style.width = `${width}%`;
        }
    }, [currentQuestionIndex, totalQuestions]);

    const handleSelectAnswer = (answerIndex: number) => {
        if (isSubmitted) return;
        const newAnswers = [...selectedAnswers];
        newAnswers[currentQuestionIndex] = answerIndex;
        setSelectedAnswers(newAnswers);
    };

    const handlePrevious = () => {
        if (currentQuestionIndex > 0) {
            updateQuestionTime();
            setCurrentQuestionIndex(currentQuestionIndex - 1);

            // Mark start time for the previous question we are moving TO
            const newStartTimes = [...questionStartTimes];
            newStartTimes[currentQuestionIndex - 1] = Date.now();
            setQuestionStartTimes(newStartTimes);
        }
    };

    const handleNext = () => {
        if (currentQuestionIndex < totalQuestions - 1) {
            updateQuestionTime();
            setCurrentQuestionIndex(currentQuestionIndex + 1);

            // Mark start time for the next question we are moving TO
            const newStartTimes = [...questionStartTimes];
            newStartTimes[currentQuestionIndex + 1] = Date.now();
            setQuestionStartTimes(newStartTimes);
        }
    };

    const handleSubmit = async () => {
        updateQuestionTime(); // Update time for the very last question
        const timeSpentSeconds = Math.round((Date.now() - startTime) / 1000);

        try {
            const response = await apiFetch(`${API_URL}/api/topic-quizzes/${quiz._id}/submit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    studentId,
                    answers: selectedAnswers.map(a => a ?? -1),
                    timeSpentSeconds,
                    questionTimes: questionTimes,
                    teacherId: quiz.teacherId || null,
                    classId: quiz.classId || null
                })
            });

            if (response.ok) {
                const resultData = await response.json();
                setResult(resultData);
                setIsSubmitted(true);

                // Also stop quiz tracking on submit
                if (socket) {
                    socket.emit('stop_quiz', {
                        studentId: studentId.toString(),
                        quizId: quiz._id,
                        classId: quiz.classId
                    });
                }

                if (onComplete) onComplete(resultData);
            } else {
                const errorData = await response.json();
                alert('Test kaydedilemedi: ' + (errorData.error || 'Bilinmeyen hata'));
            }
        } catch (error: any) {
            console.error('Error submitting quiz:', error);
            alert('Bağlantı hatası: ' + error.message);
        }
    };

    const getOptionLetter = (index: number) => ['A', 'B', 'C', 'D', 'E'][index];

    // Result screen
    if (isSubmitted && result && !showReview) {
        const percentage = result.score;
        const isGood = percentage >= 70;
        const isMedium = percentage >= 40 && percentage < 70;

        return (
            <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
                <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden animate-fade-in">
                    {/* Header */}
                    <div className={`px-6 py-8 text-center ${isGood ? 'bg-gradient-to-r from-green-500 to-emerald-600' :
                        isMedium ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
                            'bg-gradient-to-r from-red-500 to-rose-600'
                        }`}>
                        <div className="text-6xl mb-4">
                            {isGood ? '🎉' : isMedium ? '💪' : '📚'}
                        </div>
                        <h2 className="text-3xl font-bold text-white mb-2">
                            %{percentage}
                        </h2>
                        <p className="text-white/80">
                            {isGood ? 'Harika!' : isMedium ? 'Fena değil!' : 'Biraz daha çalışmalısın!'}
                        </p>
                    </div>

                    {/* Stats */}
                    <div className="p-6">
                        <div className="grid grid-cols-4 gap-4 mb-6">
                            <div className="text-center p-3 bg-green-50 rounded-xl">
                                <div className="text-xl font-bold text-green-600">{result.correctCount}</div>
                                <div className="text-[10px] text-green-700 font-medium">Doğru</div>
                            </div>
                            <div className="text-center p-3 bg-red-50 rounded-xl">
                                <div className="text-xl font-bold text-red-600">{result.incorrectCount}</div>
                                <div className="text-[10px] text-red-700 font-medium">Yanlış</div>
                            </div>
                            <div className="text-center p-3 bg-gray-50 rounded-xl">
                                <div className="text-xl font-bold text-gray-600">{result.unansweredCount}</div>
                                <div className="text-[10px] text-gray-700 font-medium">Boş</div>
                            </div>
                            <div className="text-center p-3 bg-indigo-50 rounded-xl">
                                <div className="text-xl font-bold text-indigo-600">{result.topicNet.toFixed(2)}</div>
                                <div className="text-[10px] text-indigo-700 font-medium">Net</div>
                            </div>
                        </div>

                        {/* Buttons */}
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowReview(true)}
                                className="flex-1 py-3 bg-indigo-100 text-indigo-700 rounded-xl font-bold hover:bg-indigo-200 transition-colors"
                            >
                                Cevapları İncele
                            </button>
                            <button
                                onClick={onClose}
                                className="flex-1 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors"
                            >
                                Kapat
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Review screen
    if (showReview && result) {
        return (
            <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
                <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] shadow-2xl overflow-hidden flex flex-col">
                    {/* Header */}
                    <div className="px-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 flex justify-between items-center">
                        <h2 className="text-xl font-bold text-white">Cevap Anahtarı</h2>
                        <button onClick={onClose} className="p-2 text-white/80 hover:text-white rounded-lg" title="Kapat">
                            <XMarkIcon className="w-6 h-6" />
                        </button>
                    </div>

                    {/* Questions Review */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-4">
                        {quiz.questions.map((question, idx) => {
                            const userAnswer = selectedAnswers[idx];
                            const correctAnswer = result.correctAnswers[idx];
                            const isCorrect = userAnswer === correctAnswer;
                            const isUnanswered = userAnswer === null || userAnswer === -1;

                            return (
                                <div key={idx} className={`p-4 rounded-xl border-2 ${isCorrect ? 'border-green-200 bg-green-50' :
                                    isUnanswered ? 'border-gray-200 bg-gray-50' :
                                        'border-red-200 bg-red-50'
                                    }`}>
                                    <div className="flex items-start gap-3 mb-3">
                                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${isCorrect ? 'bg-green-500 text-white' :
                                            isUnanswered ? 'bg-gray-400 text-white' :
                                                'bg-red-500 text-white'
                                            }`}>
                                            {idx + 1}
                                        </span>
                                        <p className="text-gray-800 font-medium flex-1">{question.questionText}</p>
                                    </div>
                                    <div className="space-y-2 ml-11">
                                        {question.options.map((option, optIdx) => {
                                            const isUserAnswer = userAnswer === optIdx;
                                            const isCorrectOption = correctAnswer === optIdx;

                                            return (
                                                <div
                                                    key={optIdx}
                                                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${isCorrectOption ? 'bg-green-100 text-green-800 font-bold' :
                                                        isUserAnswer && !isCorrect ? 'bg-red-100 text-red-800 line-through' :
                                                            'text-gray-600'
                                                        }`}
                                                >
                                                    <span className="font-bold">{getOptionLetter(optIdx)})</span>
                                                    <span>{option}</span>
                                                    {isCorrectOption && <CheckCircleIcon className="w-4 h-4 ml-auto text-green-600" />}
                                                    {isUserAnswer && !isCorrect && <XCircleIcon className="w-4 h-4 ml-auto text-red-600" />}
                                                </div>
                                            );
                                        })}

                                        {/* Solution Section */}
                                        {(question.solutionImage || question.solutionVideoId) && (
                                            <div className="mt-4 p-4 bg-blue-50/50 rounded-xl border border-blue-100">
                                                <h5 className="text-xs font-bold text-blue-700 mb-2 flex items-center gap-2">
                                                    💡 Çözüm Açıklaması
                                                </h5>
                                                {question.solutionImage && (
                                                    <img
                                                        src={question.solutionImage}
                                                        className="w-full rounded-lg mb-3 border border-blue-100 shadow-sm"
                                                        alt="Çözüm Görseli"
                                                    />
                                                )}
                                                {question.solutionVideoId && (
                                                    <div className="aspect-video rounded-lg overflow-hidden border border-blue-100 shadow-sm">
                                                        <iframe
                                                            width="100%"
                                                            height="100%"
                                                            src={youtubeGommeAdresi(question.solutionVideoId)}
                                                            title="Soru Çözümü"
                                                            frameBorder="0"
                                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                            allowFullScreen
                                                        ></iframe>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Footer */}
                    <div className="px-6 py-4 border-t border-gray-200">
                        <button
                            onClick={onClose}
                            className="w-full py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors"
                        >
                            Kapat
                        </button>
                    </div>
                </div>
            </div>
        );
    }



    // Quiz taking screen
    return (
        <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 flex justify-between items-center">
                    <div>
                        <h2 className="text-lg font-bold text-white">{quiz.topic}</h2>
                        <p className="text-white/70 text-sm">{quiz.subject} • {quiz.examType}</p>
                    </div>
                    <button onClick={onClose} className="p-2 text-white/80 hover:text-white rounded-lg" title="Kapat">
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>

                {/* Progress */}
                <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-600">
                            Soru {currentQuestionIndex + 1} / {totalQuestions}
                        </span>
                        <span className="text-sm font-medium text-indigo-600">
                            {answeredCount} cevaplandı
                        </span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                            ref={progressRef}
                            className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all"
                        />
                    </div>
                </div>

                {/* Question */}
                <div className="p-6">
                    <p className="text-lg font-medium text-gray-800 mb-6">{currentQuestion.questionText}</p>

                    {currentQuestion.questionImage && (
                        <div className="mb-6 rounded-2xl overflow-hidden border border-gray-100 shadow-sm bg-gray-50">
                            <img
                                src={currentQuestion.questionImage}
                                alt="Soru Görseli"
                                className="max-w-full mx-auto max-h-[300px] object-contain"
                            />
                        </div>
                    )}

                    {/* Options */}
                    <div className="space-y-3">
                        {currentQuestion.options.map((option, idx) => {
                            const isSelected = selectedAnswers[currentQuestionIndex] === idx;
                            return (
                                <button
                                    key={idx}
                                    onClick={() => handleSelectAnswer(idx)}
                                    className={`w-full flex items-center gap-4 px-4 py-3 rounded-xl text-left transition-all ${isSelected
                                        ? 'bg-indigo-100 border-2 border-indigo-500 text-indigo-800'
                                        : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100 text-gray-700'
                                        }`}
                                >
                                    <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${isSelected ? 'bg-indigo-500 text-white' : 'bg-gray-200 text-gray-600'
                                        }`}>
                                        {getOptionLetter(idx)}
                                    </span>
                                    <span className="flex-1">{option}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Navigation */}
                <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
                    <button
                        onClick={handlePrevious}
                        disabled={currentQuestionIndex === 0}
                        className={`px-6 py-2 rounded-xl font-bold transition-colors ${currentQuestionIndex === 0
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                    >
                        ← Önceki
                    </button>

                    {currentQuestionIndex === totalQuestions - 1 ? (
                        <button
                            onClick={handleSubmit}
                            className="px-8 py-2 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 transition-colors"
                        >
                            Testi Bitir ✓
                        </button>
                    ) : (
                        <button
                            onClick={handleNext}
                            className="px-6 py-2 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors"
                        >
                            Sonraki →
                        </button>
                    )}
                </div>

                {/* Question Navigator */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <div className="flex flex-wrap gap-2 justify-center">
                        {quiz.questions.map((_, idx) => {
                            const isAnswered = selectedAnswers[idx] !== null;
                            const isCurrent = idx === currentQuestionIndex;
                            return (
                                <button
                                    key={idx}
                                    onClick={() => setCurrentQuestionIndex(idx)}
                                    className={`w-8 h-8 rounded-lg text-sm font-bold transition-all ${isCurrent
                                        ? 'bg-indigo-600 text-white scale-110'
                                        : isAnswered
                                            ? 'bg-green-100 text-green-700'
                                            : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                                        }`}
                                >
                                    {idx + 1}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TopicQuizModal;
